import { useState, useCallback } from 'react';
import { useCancelable } from '.';

/** The action driven by {@link useAsynchronousAction}. */
type AsyncAction = (...args: unknown[]) => Promise<unknown>;

/** Result of {@link useAsynchronousAction}. */
interface AsynchronousAction {
  error: boolean;
  busy: boolean;
  execute: (...args: unknown[]) => void;
  clearError: () => void;
}

/**
 * Custom hook to make using asynchronous actions easier.
 *
 * @param action the action to execute
 * @param onSuccess callback that will be called after the action was executed successfully
 * @param onError callback that will be called after the action produced an error
 *
 * Callback will only be called when the component that uses this hooks is still mounted.
 *
 * @return An object with:
 * - error: boolean indicating whether an error occurred
 * - busy: boolean indicating whether the action is executing
 * - execute: function to call to start the execution of the action
 * - clearError: function to clear the error status
 */
export function useAsynchronousAction(
  action: AsyncAction | null | undefined,
  onSuccess?: (...results: unknown[]) => void,
  onError?: (...errors: unknown[]) => void
): AsynchronousAction {
  const [promise, setPromise] = useState<Promise<unknown> | null>(null);
  const [error, setError] = useState(false);

  useCancelable(
    promise,
    (...results) => {
      setPromise(null);
      if (onSuccess != null) {
        onSuccess(...results);
      }
    },
    (...errors) => {
      setPromise(null);
      setError(true);
      if (onError != null) {
        onError(...errors);
      }
    }
  );

  const clearError = useCallback(() => {
    setError(false);
  }, [setError]);

  return {
    error,
    busy: promise != null,
    execute: (...args) => {
      setError(false);
      if (action != null) {
        setPromise(action(...args));
      }
    },
    clearError,
  };
}
